import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { takeBootstrapAccounts } from "@/lib/bootstrap";

const AccountCtx = createContext(null);
const STORAGE_KEY = "op_selected_account";

export function AccountProvider({ children }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const applyAccounts = useCallback((data) => {
    setAccounts(data || []);
    setSelectedId((cur) => {
      if (cur && data.some((a) => a.id === cur)) return cur;
      const primary = data.find((a) => a.is_primary) || data[0];
      return primary ? primary.id : null;
    });
  }, []);

  const refreshAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts");
      applyAccounts(data || []);
    } catch { /* handled by interceptor */ }
    finally { setLoading(false); }
  }, [applyAccounts]);

  const initedRef = useRef(false);
  useEffect(() => {
    // Use accounts already delivered by /auth/bootstrap (no extra round-trip).
    // Guarded so React StrictMode's double-mount can't trigger a redundant fetch.
    if (initedRef.current) return;
    initedRef.current = true;
    const boot = takeBootstrapAccounts();
    if (boot && boot.length) {
      applyAccounts(boot);
      setLoading(false);
    } else {
      refreshAccounts();
    }
  }, [refreshAccounts, applyAccounts]);

  const setSelectedAccountId = useCallback((id) => {
    setSelectedId(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  }, []);

  const selectedAccount = accounts.find((a) => a.id === selectedId) || accounts[0] || null;

  return (
    <AccountCtx.Provider value={{ accounts, selectedAccount, selectedId: selectedAccount?.id || null, setSelectedAccountId, refreshAccounts, loading }}>
      {children}
    </AccountCtx.Provider>
  );
}

export const useAccounts = () => useContext(AccountCtx);

// Display label for an account type, falling back to the custom label.
export function accountTypeKey(type) {
  return ({ checking: "accounts.type_checking", savings: "accounts.type_savings", yield: "accounts.type_yield" })[type] || "accounts.type_checking";
}
